export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API ${status}`);
    this.name = "ApiError";
  }
}

export interface ApiClientConfig {
  /**
   * Base URL of apps/api. Empty string = same-origin (legacy / dev proxy).
   * Phase 2 prod = https://api.pontimed.app. Capacitor prod = same.
   *
   * Apps resolve this from their own env layer and pass it via
   * configureApiClient(). Vite reads import.meta.env.NEXT_PUBLIC_API_URL,
   * Next reads process.env.NEXT_PUBLIC_API_URL — neither is the package's
   * concern.
   */
  baseUrl: string;
  /**
   * Optional bearer-token resolver for environments without HttpOnly cookie
   * support (Capacitor on iOS WKWebView). Returns the access token string,
   * or null when none stored. The web build leaves this undefined and relies
   * on `credentials: "include"` cookies.
   */
  getBearerToken?: () => string | null | Promise<string | null>;
  /**
   * More request headers for every call (the native app's step-up receipts —
   * @pontimed/auth-client native-session.ts). A header the caller set itself
   * is never replaced. The web build leaves this undefined.
   */
  getRequestHeaders?: () =>
    | Record<string, string>
    | null
    | undefined
    | Promise<Record<string, string> | null | undefined>;
  /**
   * Sees every successful JSON answer before it is returned (the native app
   * keeps the receipts handed to it in a body). Its failures never fail the
   * call. The web build leaves this undefined.
   */
  onResponseBody?: (body: unknown) => void | Promise<void>;
  /**
   * Hook called once per refresh attempt. Returns true on success, false on
   * failure (auth-client wires this to POST /api/auth/refresh + token store).
   * If undefined, 401s are surfaced directly without retry.
   */
  refreshAccessToken?: () => Promise<boolean>;
  /**
   * Called when refresh fails OR when a 401 happens with no refresh hook.
   * Auth-client uses this to clear local session state + redirect to /login.
   */
  onAuthFailure?: () => void;
}

export interface RequestOptions {
  headers?: HeadersInit;
  signal?: AbortSignal;
  /** Skip auto-refresh on 401 (use for the refresh call itself). */
  skipAuthRefresh?: boolean;
}
